import type { CallbackTicket } from '../services/callbackTicketService';
import type { StaffNotification } from '../services/notificationService';
import { formatPhone } from './conversationFormat';
import type { Selection } from './conversationUrl';

/**
 * Pure helpers of the phone agent's Rückruf-Ticket (contract rueckruf-ticket
 * 2026-10-01): where a ticket leads, how its number reads, and the check of the
 * reception's notification address.
 */

/** The ticket's call: the stored transcript once the call record arrived, the call by its sid before. */
export function ticketSelection(ticket: Pick<CallbackTicket, 'conversation_id' | 'call_sid'>): Selection {
  return ticket.conversation_id != null
    ? { kind: 'conversation', id: ticket.conversation_id }
    : { kind: 'call', sid: ticket.call_sid };
}

/** Verläufe link of the ticket's call (`?c=` / `?call=`, contract §5). */
export function ticketUrl(ticket: Pick<CallbackTicket, 'conversation_id' | 'call_sid'>): string {
  const params: Record<string, string> =
    ticket.conversation_id != null ? { c: String(ticket.conversation_id) } : { call: ticket.call_sid };
  return `/conversations?${new URLSearchParams(params).toString()}`;
}

/** „+4915112345678“ — the API sends digits only. */
export function ticketPhone(ticket: Pick<CallbackTicket, 'customer_phone'>): string {
  return formatPhone(ticket.customer_phone);
}

export function findTicketForCall(tickets: CallbackTicket[], callSid: string | null | undefined): CallbackTicket | null {
  if (!callSid) return null;
  return tickets.find((t) => t.call_sid === callSid) ?? null;
}

/**
 * A callback wish raised in a phone call (bell kind `callback_requested` with
 * a `call_sid` and no WhatsApp contact) — as opposed to the WhatsApp one.
 */
export function isPhoneCallbackNotification(item: Pick<StaffNotification, 'kind' | 'contact_key' | 'call_sid'>): boolean {
  return item.kind === 'callback_requested' && !item.contact_key && Boolean(item.call_sid);
}

/** Same limit as the backend (`CALLBACK_TICKET_EMAIL_MAX`). */
export const NOTIFY_EMAIL_MAX = 254;

/** local@domain.tld — no spaces, no second „@“, no empty or doubled dots in the domain. */
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;

/**
 * Why the reception address cannot be saved, or null when it can. Empty is
 * fine (= no mail). One address only: the backend puts it into a mail header
 * and rejects lists and line breaks (422).
 */
export function notifyEmailError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/[,;\r\n]/.test(trimmed)) return 'Bitte nur eine E-Mail-Adresse angeben.';
  if (trimmed.length > NOTIFY_EMAIL_MAX) return `Höchstens ${NOTIFY_EMAIL_MAX} Zeichen.`;
  if (!EMAIL.test(trimmed)) return 'Bitte eine gültige E-Mail-Adresse angeben.';
  return null;
}
